import React, { useCallback, useMemo, useState } from 'react';
import { useCpOrderInvoiceLineItemsTableState } from './useCpOrderInvoiceLineItemsTableState';
import TableTop from '../../../core/table/TableTop';
import TableList from '../../../core/table/TableList';
import TableBody from '../../../core/table/TableBody';
import TablePaging from '../../../core/table/TablePaging';
import TableHead from '../../../core/table/TableHead';
import { ngDelegate } from '../../../core/services/ng.delegate';
import { useCpOrderInvoiceLineItemsTableInit } from './useCpOrderInvoiceLineItemsTableInit';
import { useInvoiceDetailsData, useInvoiceLineItemsData } from './useInvoiceDetailsData';
import { useSelectRow } from '../../../core/table/hooks/useSelectRow';
import FlyOutMenu from '../../../core/components/FlyOutMenu';
import InvoiceLineItemDispensesTableList from './InvoiceLineItemDispensesTableList';
import { useInvoiceLineItemDispensesData } from './useInvoiceLineItemDispensesData';
import { CpOrderInvoicesDetailsLineItem } from './cp_order_invoices_details_types';
import Dialog from '../../../core/components/Dialog';
import { useReconcileModalState } from './ReconcileLinkRenderer';
import ValidInputText from '../../../core/components/validation/ValidInputText';
import cpOrderInvoicesRepo from '../data/cpOrderInvoicesRepo';
import SaveToExcelButton from '../../../core/table/actions/SaveToExcelButton';
import HelpButton from '../../../core/components/HelpButton';
import { HttpErrorIgnore } from '../../../../global';
import { useAtomValue } from 'jotai';
import { cpOrderInvoicesDetailsPageStateAtom } from './cp_order_invoices_details_atoms';
import { useFetchInvoice } from './useFetchInvoice';

export const cpOrderInvoiceLineItemsTableId = 'cpOrderInvoiceLineItemsList';

const CpOrderInvoiceLineItemsTableList = (): JSX.Element => {
    const { init } = useCpOrderInvoiceLineItemsTableInit();
    if (init) {
        return <></>;
    }
    return <TableWrapper />;
};

const TableWrapper = (): JSX.Element => {
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const { items } = useInvoiceLineItemsData();
    const { selectedRows, setSelectedRows } = useSelectRow('LineId');
    const { id, setId, modalOpen, setModalOpen, notes, setNotes, setReconciledIds } = useReconcileModalState();
    const invoice = useInvoiceDetailsData();
    const refetchInvoice = useFetchInvoice();
    const { userType } = useAtomValue(cpOrderInvoicesDetailsPageStateAtom);

    const { getListAsync } = useInvoiceLineItemDispensesData();

    const onGetExcel = (): Promise<HttpErrorIgnore | undefined> =>
        cpOrderInvoicesRepo.getLineItemsExcel(invoice.Id, invoice.VendorInvoiceNum, !!invoice.IsRebate);

    const { fltStats, paging, cols, sections, sort, filters, setFilter, setSort, setPaging, waiting } =
        useCpOrderInvoiceLineItemsTableState();

    const handleRowClick = useCallback(
        (rowItem: any): void => {
            if (!rowItem.LineId) {
                ngDelegate.ok('Line Item Backing', 'No backing dispenses available.');
                return;
            }
            getListAsync(rowItem.LineId);
            setSelectedRows([rowItem], false, true);
            setShowDialog(true);
        },
        [setSelectedRows, setShowDialog, getListAsync],
    );

    const showHelp = (): void => ngDelegate.helpLink('order-splitinvoices-detail', 'Split Invoice Detail');

    const handleFlyOutClose = useCallback((): void => {
        setSelectedRows([]);
        setShowDialog(false);
    }, [setSelectedRows, setShowDialog]);

    const selectedKey = Object.keys(selectedRows)[0];
    const selectedLineItem = selectedRows[selectedKey] as CpOrderInvoicesDetailsLineItem;
    const dispenseBackingDialogTitle = selectedLineItem
        ? `Line #${selectedLineItem?.LineNum} - ${selectedLineItem?.NDC}: ${selectedLineItem?.VendorItemName} Dispense Backing`
        : 'Dispense Backing';

    const dialogActions = useMemo(
        () => [
            {
                label: 'OK',
                // Await the true up POST before updating UI state — if the request fails,
                // the modal stays open and nothing is marked reconciled.
                onClick: async (): Promise<void> => {
                    if (id) {
                        await cpOrderInvoicesRepo.trueUpLineItem(id, notes);
                        setReconciledIds(id);
                        setModalOpen(false);
                        setId(undefined);
                        setNotes('');
                        // Re-fetch the invoice so the Reconcile button disappears
                        // for the row that was just true'd up.
                        refetchInvoice(invoice.Id);
                    }
                },
            },
            {
                label: 'CANCEL',
                onClick: (): void => {
                    setModalOpen(false);
                    setId(undefined);
                    setNotes('');
                },
                cssName: 'btn-cancel',
            },
        ],
        [setModalOpen, id, setId, notes, setNotes, setReconciledIds, refetchInvoice, invoice.Id],
    );

    const reconcileNotes = useMemo(
        () => ({
            key: 'notes',
            lbl: '',
            val: notes,
            err: '',
        }),
        [notes],
    );

    if (waiting) {
        return <></>;
    }

    return (
        <>
            <Dialog
                open={modalOpen}
                setOpen={setModalOpen}
                title="Confirm True Up"
                message="External financial reconciliation occurred. V340B will no longer attempt to reconcile selected line item."
                actions={dialogActions}
            >
                <ValidInputText
                    prop={reconcileNotes}
                    onChange={(_key: string, val: any): void => setNotes(val)}
                    placeholder="Optional notes..."
                />
            </Dialog>
            <TableList noSpacing>
                <TableTop
                    id={cpOrderInvoiceLineItemsTableId}
                    fltStats={fltStats}
                    topRight={
                        <div>
                            <SaveToExcelButton
                                waiting={waiting}
                                setWait={(): null => null}
                                onGetExcel={onGetExcel}
                            />
                            <HelpButton onClick={showHelp} />
                        </div>
                    }
                />
                <table>
                    <TableHead
                        cols={cols}
                        sections={sections}
                        sort={sort}
                        filters={filters}
                        setFilter={setFilter}
                        setSort={setSort}
                    />
                    <TableBody
                        cols={cols}
                        rows={items}
                        onClick={handleRowClick}
                    />
                </table>
                <TablePaging
                    paging={paging}
                    setPaging={setPaging}
                    fltStats={fltStats}
                />
            </TableList>
            <FlyOutMenu
                title={dispenseBackingDialogTitle}
                isOpen={showDialog}
                onClose={handleFlyOutClose}
                width={900}
                overflowY="scroll"
            >
                <InvoiceLineItemDispensesTableList
                    invoiceId={invoice.Id}
                    lineItemId={selectedLineItem?.LineId}
                    rebatesOnly={invoice.IsRebate ?? false}
                    userType={userType}
                />
            </FlyOutMenu>
        </>
    );
};

export default CpOrderInvoiceLineItemsTableList;
